/**
 * useDashboardFilters — URL-state hook for the Analytics filter bar.
 *
 * Models the GoodData filter shape:
 *   - Date range: All time | Static (from/to ISO dates) | Relative (count + unit + excludeToday)
 *   - Organization: multi-select array of organization IDs
 *   - Node: multi-select array of node IDs (only used on Active Users + Security Policies dashboards)
 *
 * Filter state lives in the URL so links + browser back-button work, and so
 * cross-widget filtering (click a country on geo map → filters every other
 * widget on the page) is just a state push. No Zustand store needed.
 *
 * Encoded as discrete query params for readability:
 *   ?dateKind=relative&dateUnit=day&dateCount=7&excludeToday=1&orgs=a,b,c&nodes=n1,n2
 */

import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

export type DateRangeUnit = 'year' | 'quarter' | 'month' | 'week' | 'day' | 'hour' | 'minute';

export type DateRange =
  | { kind: 'all-time' }
  | { kind: 'relative'; unit: DateRangeUnit; count: number; excludeToday: boolean }
  | { kind: 'static'; from: string; to: string };

export const DEFAULT_DATE_RANGE: DateRange = { kind: 'all-time' };

export interface DashboardFilters {
  dateRange: DateRange;
  orgs: string[];
  nodes: string[];
}

function parseDateRange(params: URLSearchParams): DateRange {
  const kind = params.get('dateKind');
  if (kind === 'relative') {
    const unit = (params.get('dateUnit') as DateRangeUnit | null) ?? 'day';
    const count = Number.parseInt(params.get('dateCount') ?? '7', 10);
    const excludeToday = params.get('excludeToday') === '1';
    return { kind: 'relative', unit, count: Number.isFinite(count) ? count : 7, excludeToday };
  }
  if (kind === 'static') {
    const from = params.get('dateFrom');
    const to = params.get('dateTo');
    if (from && to) return { kind: 'static', from, to };
  }
  return { kind: 'all-time' };
}

function parseList(value: string | null): string[] {
  if (!value) return [];
  return value.split(',').filter(Boolean);
}

function writeDateRange(params: URLSearchParams, range: DateRange): void {
  // Clear all date-related keys before writing.
  params.delete('dateKind');
  params.delete('dateUnit');
  params.delete('dateCount');
  params.delete('excludeToday');
  params.delete('dateFrom');
  params.delete('dateTo');
  if (range.kind === 'all-time') return;
  if (range.kind === 'relative') {
    params.set('dateKind', 'relative');
    params.set('dateUnit', range.unit);
    params.set('dateCount', String(range.count));
    if (range.excludeToday) params.set('excludeToday', '1');
    return;
  }
  params.set('dateKind', 'static');
  params.set('dateFrom', range.from);
  params.set('dateTo', range.to);
}

export function useDashboardFilters() {
  const [searchParams, setSearchParams] = useSearchParams();

  const filters: DashboardFilters = useMemo(
    () => ({
      dateRange: parseDateRange(searchParams),
      orgs: parseList(searchParams.get('orgs')),
      nodes: parseList(searchParams.get('nodes')),
    }),
    [searchParams]
  );

  const setDateRange = useCallback(
    (range: DateRange) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        writeDateRange(next, range);
        return next;
      });
    },
    [setSearchParams]
  );

  const setOrgs = useCallback(
    (orgs: string[]) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (orgs.length === 0) next.delete('orgs');
        else next.set('orgs', orgs.join(','));
        return next;
      });
    },
    [setSearchParams]
  );

  const setNodes = useCallback(
    (nodes: string[]) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (nodes.length === 0) next.delete('nodes');
        else next.set('nodes', nodes.join(','));
        return next;
      });
    },
    [setSearchParams]
  );

  const reset = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      writeDateRange(next, { kind: 'all-time' });
      next.delete('orgs');
      next.delete('nodes');
      return next;
    });
  }, [setSearchParams]);

  return { ...filters, setDateRange, setOrgs, setNodes, reset };
}

/**
 * Stable preset list used by AnalyticsFilterBar. Approximates the GoodData
 * "Relative" preset set (the full list groups by granularity: minute / hour /
 * day / week / month / quarter / year, each with rolling-N and "to date"
 * variants). Until a custom date-range picker lands, these cover the
 * common stakeholder asks; the catalog's full GoodData option set can be
 * extended here as needed.
 *
 * Note: `excludeToday` is supported by every "Relative" preset that includes
 * today. A standalone "Exclude today" checkbox is rendered alongside the
 * preset select on the AnalyticsFilterBar — toggles the flag on the active
 * preset without changing the count/unit.
 */
export const DATE_RANGE_PRESETS: ReadonlyArray<{ label: string; group: 'all' | 'hour' | 'day' | 'week' | 'month' | 'quarter' | 'year'; value: DateRange }> = [
  { label: 'All time', group: 'all', value: { kind: 'all-time' } },
  { label: 'Last hour', group: 'hour', value: { kind: 'relative', unit: 'hour', count: 1, excludeToday: false } },
  { label: 'Last 24 hours', group: 'hour', value: { kind: 'relative', unit: 'hour', count: 24, excludeToday: false } },
  { label: 'Last 7 days', group: 'day', value: { kind: 'relative', unit: 'day', count: 7, excludeToday: false } },
  { label: 'Last 14 days', group: 'day', value: { kind: 'relative', unit: 'day', count: 14, excludeToday: false } },
  { label: 'Last 30 days', group: 'day', value: { kind: 'relative', unit: 'day', count: 30, excludeToday: false } },
  { label: 'Last 4 weeks', group: 'week', value: { kind: 'relative', unit: 'week', count: 4, excludeToday: false } },
  { label: 'Last 90 days', group: 'day', value: { kind: 'relative', unit: 'day', count: 90, excludeToday: false } },
  { label: 'Last 3 months', group: 'month', value: { kind: 'relative', unit: 'month', count: 3, excludeToday: false } },
  { label: 'Last 6 months', group: 'month', value: { kind: 'relative', unit: 'month', count: 6, excludeToday: false } },
  { label: 'Last 12 months', group: 'month', value: { kind: 'relative', unit: 'month', count: 12, excludeToday: false } },
  { label: 'Last quarter', group: 'quarter', value: { kind: 'relative', unit: 'quarter', count: 1, excludeToday: false } },
  { label: 'Last 4 quarters', group: 'quarter', value: { kind: 'relative', unit: 'quarter', count: 4, excludeToday: false } },
  { label: 'Last 12 months', group: 'year', value: { kind: 'relative', unit: 'month', count: 12, excludeToday: false } },
  { label: 'Last year', group: 'year', value: { kind: 'relative', unit: 'year', count: 1, excludeToday: false } },
  { label: 'Last 2 years', group: 'year', value: { kind: 'relative', unit: 'year', count: 2, excludeToday: false } },
];

/** Group ordering + labels for the preset dropdown. */
export const DATE_RANGE_GROUPS: Array<{ key: 'all' | 'hour' | 'day' | 'week' | 'month' | 'quarter' | 'year'; label: string }> = [
  { key: 'all', label: 'All Time' },
  { key: 'hour', label: 'Hour' },
  { key: 'day', label: 'Day' },
  { key: 'week', label: 'Week' },
  { key: 'month', label: 'Month' },
  { key: 'quarter', label: 'Quarter' },
  { key: 'year', label: 'Year' },
];
